import { useAppDispatch, useAppSelector } from '@/app/hooks';
import VideoHlsView, {
  type VideoHlsViewHandle,
} from '@/components/pages/live/attendee/VideoHlsView';
import YouTubeWatchView from '@/components/pages/live/attendee/YouTubeWatchView';
import ChatView, { clearChatHistoryJitterFlag } from '@/components/pages/live/ChatView';
import {
  fetchMyPublicQuizSubmission,
  fetchPublicEvergreen,
  fetchPublicOffer,
  fetchPublicPlayback,
  fetchPublicQuitPage,
  fetchPublicQuiz,
  fetchPublicResourceState,
  fetchPublicSession,
  fetchPublicSurvey,
  fetchPublicWebinarResources,
  fetchPresenceStatus,
  postAnalyticsEvent,
  refreshEvergreenChatMode,
  submitPublicQuitPage,
} from '@/features/live/liveThunks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useChannelName } from '@/routes/AblyAuth';
import type { RootState } from '@/app/store';
import LiveReactionSection from '@/components/pages/live/attendee/LiveReactionSection';
import FooterBar from '@/components/pages/live/FootBar';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { useExitIntent } from '@/hooks/useExitIntent';
import { getBreakpointFromWidth } from '@/lib/breakpoints';
import QuitCard from '@/components/common/asset/QuitCard';
import SurveyCard from '@/components/common/asset/SurveyCard';
import QuizCard from '@/components/common/asset/QuizCard';
import OfferCard from '@/components/common/asset/OfferCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  fetchMyPublicSurveySubmission,
  fetchPublicWebinarSession,
  fetchCurrentSharedResources,
} from '@/features/webinar/webinarThunks';
import {
  applySharedResourceLiveEvent,
  clearCurrentSharedResources,
} from '@/features/webinar/webinarSlice';
import type { SharedResourcePublic } from '@/features/webinar/types';
import {
  normalizeSharedResourceEventType,
  resolveSharedResourceEventAction,
} from '@/lib/sharedResourceLiveSync';
import SharedResourceViewer from '@/components/pages/live/attendee/SharedResourceViewer';
import { useChannel } from 'ably/react';
import SplashScreen from '@/components/common/SplashScreen';
import LiveEntryError from '@/components/common/LiveEntryError';
import { useElapsedFlag } from '@/hooks/useElapsedFlag';
import { LIVE_ENTRY_TIMEOUT_MS, isLiveEntryPending } from '@/lib/liveEntryRouting';
import {
  clearPublicOffer,
  clearPublicSurvey,
  clearPublicQuiz,
  setYoutubeClosePending,
  applyEvergreenChatModeChange,
  openOfferEmbed,
} from '@/features/live/liveSlice';
import { shouldShowOfferEmbed } from '@/lib/offerEmbed';
import {
  readChatModeChange,
  replayedChatModeFor,
  resolveAttendeeChatMode,
} from '@/lib/evergreenChatMode';
import { isStreamAvailabilityUpdate } from '@/lib/livePlaybackRevival';
import { shouldPollLivePlayback } from '@/lib/livePlaybackPolling';
import { DateTime } from 'luxon';
import { formatPlannedLiveDate } from '@/lib/formatPlannedDate';
import { createLiveEventDeduplicator, normalizeLiveBatchMessage } from '@/lib/liveEventBatch';
import { readCriticalBackupRedirectUrl } from '@/lib/criticalBackupRedirect';
import { liveService } from '@/services/live.service';
import { cn } from '@/lib/utils';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import { getSubscriptionAttendeeChatIdentity } from '@/lib/publicLiveJwt';
import { Loader2, Maximize2, Minimize2, PictureInPicture2, Volume2 } from 'lucide-react';
import {
  enterPictureInPicture,
  exitPictureInPicture,
  exitVideoFullscreen,
  getFullscreenElement,
  isInPictureInPicture,
  isMobileViewer,
  isVideoPlaying,
  requestVideoFullscreen,
  supportsElementFullscreen,
  supportsPictureInPicture,
} from '@/lib/videoPresentation';
import { clampEvergreenPosition, resolveEvergreenSeekCorrection } from '@/lib/videoSeekGuard';
import {
  evergreenControlsBoxClassName,
  evergreenVideoBoxClassName,
} from '@/lib/evergreenFrameLayout';
import { useEvergreenVideoSource } from '@/hooks/useEvergreenVideoSource';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';

// Plafond du report de la redirection thank-you pour un live YouTube : garde-fou si la position
// de lecture est indisponible/aberrante, pour ne jamais bloquer l'attendee indéfiniment.
const YOUTUBE_CLOSE_MAX_DELAY_MS = 60000;

export default function AttendeeLivePage() {
  const { t } = useWebinarT('webinars');
  // Les libellés des contrôles vidéo vivent dans `common`, partagés avec le player du direct.
  const { t: tCommon } = useWebinarT('common');
  const webinarLng = useWebinarLng();
  const dispatch = useAppDispatch();
  const liveEventDeduplicatorRef = useRef(createLiveEventDeduplicator());
  const videoHlsViewRef = useRef<VideoHlsViewHandle>(null);
  const evergreenVideoRef = useRef<HTMLVideoElement>(null);
  const evergreenFrameRef = useRef<HTMLDivElement>(null);
  /** Seeks demandés par le code et pas encore consommés, pour ne pas les prendre pour un scrub. */
  const evergreenProgrammaticSeeksRef = useRef(0);
  const lastFetchedLiveOfferIdRef = useRef<number | null>(null);
  const lastFetchedLiveSurveyIdRef = useRef<number | null>(null);
  const lastFetchedLiveQuizIdRef = useRef<number | null>(null);
  const youtubeCloseTimeoutRef = useRef<number | null>(null);
  const { webinarId: webinarIdParam, sessionIdentifier: sessionIdentifierParam } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();

  const channelName = useChannelName();

  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const sessionIdentifier = sessionIdentifierParam;
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const attendeeInfo = useAppSelector((s: RootState) => s.live.attendeeInfo);
  const chatIdentityFromJwt = useMemo(() => getSubscriptionAttendeeChatIdentity(token), [token]);
  const chatUserId = attendeeInfo?.id?.toString() || chatIdentityFromJwt?.userId || '';
  const chatUserName = attendeeInfo?.firstName ?? chatIdentityFromJwt?.userName ?? '';
  // Barre de réactions émojis spectateur masquée temporairement (retrait à venir)
  const showSpectatorReactions: boolean = false;
  const publicSession = useAppSelector((s: RootState) => s.live.publicSession);
  const publicSessionError = useAppSelector((s: RootState) => s.live.publicSessionError);
  const publicResourceStates = useAppSelector((s: RootState) => s.live.publicResourceStates);
  const publicQuitPage = useAppSelector((s: RootState) => s.live.publicQuitPage);
  const publicSurvey = useAppSelector((s: RootState) => s.live.publicSurvey);
  const publicQuiz = useAppSelector((s: RootState) => s.live.publicQuiz);
  const publicQuizSubmission = useAppSelector((s: RootState) => s.live.publicQuizSubmission);
  const publicOffer = useAppSelector((s: RootState) => s.live.publicOffer);
  const openedOfferEmbedId = useAppSelector((s: RootState) => s.live.openedOfferEmbedId);
  const currentPublicSurveySubmission = useAppSelector(
    (s: RootState) => s.webinar.currentPublicSurveySubmission,
  );

  const hasUserSubmittedQuiz =
    publicQuizSubmission?.templateId === publicQuiz?.id &&
    (publicQuizSubmission?.answers?.length ?? 0) > 0;
  const hasUserSubmittedSurvey =
    currentPublicSurveySubmission?.templateId === publicSurvey?.id &&
    (currentPublicSurveySubmission?.answers?.length ?? 0) > 0;
  const surveyResultsByTemplateId = useAppSelector(
    (s: RootState) => s.live.surveyResultsByTemplateId ?? {},
  );
  const visualIdentity = useAppSelector((s: RootState) => s.live.publicSession?.visualIdentity);
  const presenceStatus = useAppSelector((s: RootState) => s.live.presenceStatus);
  const playbackInfo = useAppSelector((s: RootState) => s.live.playbackInfo);
  const navigate = useNavigate();

  const effectiveSessionIdentifier = useMemo(() => {
    if (
      playbackInfo &&
      webinarId != null &&
      Number(playbackInfo.webinarId) === webinarId &&
      playbackInfo.sessionIdentifier?.trim()
    ) {
      return playbackInfo.sessionIdentifier;
    }
    return sessionIdentifier ?? '';
  }, [playbackInfo, webinarId, sessionIdentifier]);

  const hasLivePlaybackUrl = useMemo(() => {
    if (!playbackInfo || webinarId == null || Number(playbackInfo.webinarId) !== webinarId) {
      return false;
    }
    const p = playbackInfo;
    return !!(
      p.hlsLiveUrl?.trim() ||
      p.desktop?.hlsLiveUrl?.trim() ||
      p.mobile?.hlsLiveUrl?.trim() ||
      p.tablet?.hlsLiveUrl?.trim()
    );
  }, [playbackInfo, webinarId]);

  useEffect(() => {
    if (
      !webinarId ||
      !token ||
      !sessionIdentifier ||
      !playbackInfo?.sessionIdentifier ||
      Number(playbackInfo.webinarId) !== webinarId
    ) {
      return;
    }
    if (playbackInfo.sessionIdentifier === sessionIdentifier) return;
    navigate(
      `/live/${webinarId}/${playbackInfo.sessionIdentifier}?token=${encodeURIComponent(token)}`,
      {
        replace: true,
      },
    );
  }, [webinarId, sessionIdentifier, playbackInfo, token, navigate]);

  // EVERGREEN
  const sessionType = publicSession?.session?.sessionType;
  const isEvergreen = sessionType === 'EVERGREEN';
  const evergreenChatModeSync = useAppSelector((s: RootState) => s.live.evergreenChatModeSync);
  const publicEvergreen = useAppSelector((s: RootState) => s.live.publicEvergreen);

  const isYouTube = publicSession?.session?.transmissionMode === 'YOUTUBE';
  const youtubeUrl = publicSession?.session?.youtubeUrl;
  const sessionStartedAtMs = useMemo(() => {
    const startedAt = publicSession?.session?.startedAt;
    if (!startedAt) return null;
    const parsed = Date.parse(startedAt);
    return Number.isNaN(parsed) ? null : parsed;
  }, [publicSession?.session?.startedAt]);

  // Instant UTC de mise à l'antenne YouTube (renvoyé sans 'Z' par le back, donc parsé en UTC explicite).
  // Sert d'ancre pour caler l'affichage des interactions sur la position de lecture (retardée) du live YouTube.
  const youtubeActualStartTimeMs = useMemo(() => {
    const actualStartTime = publicSession?.session?.youtubeActualStartTime;
    if (!actualStartTime) return null;
    const parsed = DateTime.fromISO(actualStartTime, { zone: 'utc' });
    return parsed.isValid ? parsed.toMillis() : null;
  }, [publicSession?.session?.youtubeActualStartTime]);

  useEffect(() => {
    return () => {
      if (youtubeCloseTimeoutRef.current) {
        window.clearTimeout(youtubeCloseTimeoutRef.current);
      }
    };
  }, []);

  const formattedPlannedDate = useMemo(
    () => formatPlannedLiveDate(publicSession?.session.utcPlannedDate, webinarLng),
    [publicSession?.session.utcPlannedDate, webinarLng],
  );
  const liveSurvey = publicResourceStates?.find(
    (r) => r.resourceType === 'SURVEY_TEMPLATE' && r.live,
  );
  const liveQuiz = publicResourceStates?.find((r) => r.resourceType === 'QUIZ_TEMPLATE' && r.live);
  const liveOffer = publicResourceStates?.find(
    (r) => r.resourceType === 'OFFER_TEMPLATE' && r.live,
  );

  const activeSurvey =
    liveSurvey && publicSurvey && publicSurvey.id === liveSurvey.resourceId
      ? {
          resourceId: liveSurvey.resourceId,
          isVisible: true,
          showResults: !!surveyResultsByTemplateId[publicSurvey.id],
        }
      : null;

  const activeQuiz =
    liveQuiz && publicQuiz && publicQuiz.id === liveQuiz.resourceId
      ? { resourceId: liveQuiz.resourceId, isVisible: true }
      : null;

  const activeOffer =
    liveOffer && publicOffer && publicOffer.id === liveOffer.resourceId
      ? { resourceId: liveOffer.resourceId, isVisible: true }
      : null;

  /** Une offre / sondage / quizz est affichée : le plein écran ne doit pas la masquer. */
  const hasVisibleInteraction = !!(activeOffer || activeSurvey || activeQuiz);

  const EXIT_INTENT_COOLDOWN_MS = 30 * 60 * 1000;

  const getExitIntentStorageKey = () =>
    webinarId != null && sessionIdentifier
      ? `welya-exit-intent-${webinarId}-${sessionIdentifier}`
      : null;

  const isExitIntentInCooldown = () => {
    const key = getExitIntentStorageKey();
    if (!key) return false;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const shownAt = parseInt(raw, 10);
      return Date.now() - shownAt < EXIT_INTENT_COOLDOWN_MS;
    } catch {
      return false;
    }
  };

  const [webinarConfigOverride, setWebinarConfigOverride] = useState<{
    displayParticipantsMinThreshold?: number;
    displayParticipantsCountEnabled?: boolean;
    chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
  } | null>(null);
  const [exitIntentCooldown, setExitIntentCooldown] = useState(false);
  const [hasReached2Min, setHasReached2Min] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false,
  );
  const [target, setTarget] = useState<'mobile' | 'desktop'>(() =>
    typeof window !== 'undefined' ? getBreakpointFromWidth(window.innerWidth) : 'desktop',
  );
  useEffect(() => {
    const onResize = () => setTarget(getBreakpointFromWidth(window.innerWidth));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const attendeeCriticalBackupUrl = publicSession?.session?.criticalBackupUrl;
  useEffect(() => {
    if (!attendeeCriticalBackupUrl) return;
    globalThis.location.replace(attendeeCriticalBackupUrl);
  }, [attendeeCriticalBackupUrl]);

  useEffect(() => {
    setExitIntentCooldown(isExitIntentInCooldown());
  }, [webinarId, sessionIdentifier]);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const canShowExitIntentPopup =
    !!publicQuitPage?.showPopup && !exitIntentCooldown && hasReached2Min;

  const { showExitIntent, reset } = useExitIntent({
    enabled: canShowExitIntentPopup && !isMobile,
  });
  const [exitIntentDialogOpen, setExitIntentDialogOpen] = useState(false);
  const [liveParticipantCount, setLiveParticipantCount] = useState(0);
  const [currentReactions, setCurrentReactions] = useState<
    Array<{
      id: string;
      text: string;
      left: number;
      drift: number;
      rotateStart: number;
      rotateEnd: number;
    }>
  >([]);
  const [isEvergreenMuted, setIsEvergreenMuted] = useState(true);
  const [evergreenFooterSeconds, setEvergreenFooterSeconds] = useState(0);
  // Temporaire : spinner tant que la vidéo evergreen (gros mp4 non caché au CDN) n'a pas de frame.
  const [isEvergreenVideoLoading, setIsEvergreenVideoLoading] = useState(true);
  const [showEvergreenControls, setShowEvergreenControls] = useState(false);
  const [canUseEvergreenPip, setCanUseEvergreenPip] = useState(false);
  const [isEvergreenInPip, setIsEvergreenInPip] = useState(false);
  const [canUseEvergreenFullscreen, setCanUseEvergreenFullscreen] = useState(false);
  const [isEvergreenFullscreen, setIsEvergreenFullscreen] = useState(false);
  const hideEvergreenControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier) {
      return;
    }

    dispatch(
      fetchPublicWebinarSession({
        webinarId: Number(webinarId),
        sessionIdentifier: effectiveSessionIdentifier,
      }),
    );
  }, [webinarId, effectiveSessionIdentifier, dispatch]);

  useEffect(() => {
    if (sessionType === 'EVERGREEN' && webinarId != null && effectiveSessionIdentifier) {
      dispatch(fetchPublicEvergreen({ webinarId, sessionIdentifier: effectiveSessionIdentifier }));
    }
  }, [sessionType, webinarId, effectiveSessionIdentifier, dispatch]);

  useEffect(() => {
    if (showExitIntent && canShowExitIntentPopup) {
      setExitIntentDialogOpen(true);
      const key = getExitIntentStorageKey();
      if (key) {
        try {
          localStorage.setItem(key, Date.now().toString());
          setExitIntentCooldown(true);
        } catch {}
      }
    }
  }, [showExitIntent, canShowExitIntentPopup]);

  useEffect(() => {
    if (webinarId && effectiveSessionIdentifier && token) {
      dispatch(
        fetchPublicPlayback({
          webinarId,
          sessionIdentifier: effectiveSessionIdentifier,
          authorization: token,
        }),
      );
      dispatch(
        fetchPublicSession({
          webinarId,
          sessionIdentifier: effectiveSessionIdentifier,
        }),
      );
      dispatch(
        fetchPublicResourceState({
          webinarId,
          sessionIdentifier: effectiveSessionIdentifier,
          authorization: token,
        }),
      );
      dispatch(
        fetchPublicQuitPage({
          webinarId,
          authorization: token,
        }),
      );

      dispatch(
        fetchPublicWebinarResources({
          webinarId,
          authorization: token,
        }),
      );
    }
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }

    if (!shouldPollLivePlayback({ isEvergreen, isYouTube, hasLivePlaybackUrl })) {
      return;
    }

    const intervalMs = 2500;
    const id = setInterval(() => {
      void dispatch(
        fetchPublicPlayback({
          webinarId,
          sessionIdentifier: effectiveSessionIdentifier,
          authorization: token,
        }),
      );
    }, intervalMs);

    return () => clearInterval(id);
  }, [
    webinarId,
    effectiveSessionIdentifier,
    token,
    dispatch,
    hasLivePlaybackUrl,
    isEvergreen,
    isYouTube,
  ]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }
    void dispatch(
      fetchPresenceStatus({
        webinarId,
        sessionIdentifier: effectiveSessionIdentifier,
        authorization: token,
      }),
    );
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (presenceStatus?.liveCount !== undefined) {
      setLiveParticipantCount(presenceStatus.liveCount);
    }
  }, [presenceStatus?.liveCount]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }

    const liveOffer = publicResourceStates.find(
      (r) => r.resourceType === 'OFFER_TEMPLATE' && r.live,
    );
    if (liveOffer) {
      if (lastFetchedLiveOfferIdRef.current !== liveOffer.resourceId) {
        lastFetchedLiveOfferIdRef.current = liveOffer.resourceId;
        dispatch(
          fetchPublicOffer({
            webinarId,
            offerId: liveOffer.resourceId,
            authorization: token,
          }),
        );
      }
    } else {
      lastFetchedLiveOfferIdRef.current = null;
    }

    const liveSurvey = publicResourceStates.find(
      (r) => r.resourceType === 'SURVEY_TEMPLATE' && r.live,
    );
    if (liveSurvey) {
      if (lastFetchedLiveSurveyIdRef.current !== liveSurvey.resourceId) {
        lastFetchedLiveSurveyIdRef.current = liveSurvey.resourceId;
        dispatch(
          fetchPublicSurvey({
            webinarId,
            surveyId: liveSurvey.resourceId,
            authorization: token,
          }),
        );
        dispatch(
          fetchMyPublicSurveySubmission({
            webinarId,
            templateId: liveSurvey.resourceId,
            authorization: token,
          }),
        );
      }
    } else {
      lastFetchedLiveSurveyIdRef.current = null;
    }

    const liveQuiz = publicResourceStates.find((r) => r.resourceType === 'QUIZ_TEMPLATE' && r.live);
    if (liveQuiz) {
      if (lastFetchedLiveQuizIdRef.current !== liveQuiz.resourceId) {
        lastFetchedLiveQuizIdRef.current = liveQuiz.resourceId;
        dispatch(
          fetchPublicQuiz({
            webinarId,
            quizId: liveQuiz.resourceId,
            authorization: token,
          }),
        );
        dispatch(
          fetchMyPublicQuizSubmission({
            webinarId,
            templateId: liveQuiz.resourceId,
            authorization: token,
          }),
        );
      }
    } else {
      lastFetchedLiveQuizIdRef.current = null;
    }
  }, [webinarId, effectiveSessionIdentifier, token, publicResourceStates, dispatch]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }
    void dispatch(
      fetchCurrentSharedResources({
        webinarId,
        sessionIdentifier: effectiveSessionIdentifier,
        authorization: token,
      }),
    );
    return () => {
      dispatch(clearCurrentSharedResources());
    };
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  // Appelé quand la lecture YouTube saute en avant (clic « En direct » / rattrapage auto). On
  // resynchronise l'affichage sur l'état réel des ressources renvoyé par le back, au lieu de laisser
  // les events en attente se libérer en rafale (ce qui faisait clignoter/sauter les offres).
  const reconcileToLiveResourceState = useCallback(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }
    void dispatch(
      fetchPublicResourceState({
        webinarId,
        sessionIdentifier: effectiveSessionIdentifier,
        authorization: token,
      }),
    ).then((result) => {
      if (!fetchPublicResourceState.fulfilled.match(result)) {
        return;
      }
      const states = result.payload ?? [];
      // L'effet sur publicResourceStates ré-affiche les ressources actives ; ici on masque
      // explicitement celles qui ne le sont plus (sinon le masquage ne venait que des events purgés).
      if (!states.some((r) => r.resourceType === 'OFFER_TEMPLATE' && r.live)) {
        dispatch(clearPublicOffer());
      }
      if (!states.some((r) => r.resourceType === 'SURVEY_TEMPLATE' && r.live)) {
        dispatch(clearPublicSurvey());
      }
      if (!states.some((r) => r.resourceType === 'QUIZ_TEMPLATE' && r.live)) {
        dispatch(clearPublicQuiz());
      }
    });
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }

    dispatch(
      postAnalyticsEvent({
        webinarId,
        sessionIdentifier: effectiveSessionIdentifier,
        authorization: token,
        input: {
          eventType: 'JOINED',
          payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
          occurredAt: new Date().toISOString(),
        },
      }),
    );
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier || !token) {
      return;
    }

    const intervalId = setInterval(() => {
      dispatch(
        postAnalyticsEvent({
          webinarId,
          sessionIdentifier: effectiveSessionIdentifier,
          authorization: token,
          input: {
            eventType: 'HEARTBEAT',
            payloadJson: JSON.stringify({ timeNow: new Date().getTime() }),
            occurredAt: new Date().toISOString(),
          },
        }),
      );
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [webinarId, effectiveSessionIdentifier, token, dispatch]);

  const handleExitIntentClose = () => {
    setExitIntentDialogOpen(false);
    reset();
  };

  const handleExitIntentSubmit = async (selectedReasonIds: number[], otherReasonText?: string) => {
    const sessionId = publicSession?.session?.id;
    if (!webinarId || !token || !sessionId) {
      return;
    }

    try {
      await dispatch(
        submitPublicQuitPage({
          webinarId,
          authorization: token,
          input: {
            sessionId,
            reasonIds: selectedReasonIds,
            otherReasonText: otherReasonText || null,
          },
        }),
      ).unwrap();
      window.close();
      handleExitIntentClose();
    } catch (error) {
      console.error('Failed to submit quit page:', error);
    }
  };

  const onLiveChannelMessage = useCallback(
    (message: { name?: string; data?: unknown }) => {
      if (!channelName || !message) return;

      const normalizedMessages = normalizeLiveBatchMessage(message);

      for (const currentMessage of normalizedMessages) {
        // Le buffer Redis du back peut republier un lot déjà envoyé : sans ce filtre, une même
        // réaction s'affiche deux fois.
        if (!liveEventDeduplicatorRef.current.shouldProcess(currentMessage.eventId)) {
          continue;
        }
        const data: unknown = currentMessage.data;

        // Redirection de secours : elle part à réception. La faire dépendre d'un refetch de session
        // ou de la fin du live laissait sur place tout spectateur dont l'onglet dormait, et le
        // report de lecture des players pouvait la perdre.
        const criticalRedirectUrl = readCriticalBackupRedirectUrl(currentMessage.name, data);
        if (criticalRedirectUrl) {
          globalThis.location.replace(criticalRedirectUrl);
          return;
        }

        if (currentMessage.name === 'presence-update') {
          const live = parsePresenceUpdateLiveCount(data);
          if (live !== null) {
            setLiveParticipantCount(live);
          }
        }

        if (currentMessage.name === 'live-reaction') {
          if (
            data &&
            typeof data === 'object' &&
            'text' in data &&
            (data as { text?: unknown }).text
          ) {
            const text = String((data as { text: unknown }).text);
            const reactionId = crypto.randomUUID();
            const randomLeft = Math.random() * 20;
            const drift = (Math.random() - 0.5) * 140;
            const rotateStart = (Math.random() - 0.5) * 24;
            const rotateEnd = (Math.random() - 0.5) * 24;
            setCurrentReactions((prev) => [
              ...prev,
              {
                id: reactionId,
                text,
                left: randomLeft,
                drift,
                rotateStart,
                rotateEnd,
              },
            ]);

            setTimeout(() => {
              setCurrentReactions((prev) => prev.filter((reaction) => reaction.id !== reactionId));
            }, 3000);
          }
        }

        // EVERGREEN
        if (
          isEvergreen &&
          currentMessage.name === 'live-event' &&
          data &&
          typeof data === 'object'
        ) {
          const d = data as {
            resourceType?: string;
            eventType?: string;
            reaction?: string;
            payloadJson?: unknown;
          };
          if (d.resourceType === 'INLINE' && d.eventType === 'LIVE_REACTION') {
            let reactionText: string | undefined;
            if (typeof d.reaction === 'string' && d.reaction.trim().length > 0) {
              reactionText = d.reaction.trim();
            } else if (d.payloadJson) {
              try {
                const payload =
                  typeof d.payloadJson === 'string' ? JSON.parse(d.payloadJson) : d.payloadJson;
                if (
                  payload &&
                  typeof payload === 'object' &&
                  typeof (payload as { reaction?: unknown }).reaction === 'string' &&
                  String((payload as { reaction: string }).reaction).trim().length > 0
                ) {
                  reactionText = String((payload as { reaction: string }).reaction).trim();
                }
              } catch {
                // ignore parse error
              }
            }

            if (reactionText) {
              const reactionId = crypto.randomUUID();
              const randomLeft = Math.random() * 20;
              const drift = (Math.random() - 0.5) * 140;
              const rotateStart = (Math.random() - 0.5) * 24;
              const rotateEnd = (Math.random() - 0.5) * 24;
              setCurrentReactions((prev) => [
                ...prev,
                {
                  id: reactionId,
                  text: reactionText,
                  left: randomLeft,
                  drift,
                  rotateStart,
                  rotateEnd,
                },
              ]);

              setTimeout(() => {
                setCurrentReactions((prev) =>
                  prev.filter((reaction) => reaction.id !== reactionId),
                );
              }, 3000);
            }
          }
        }

        if (
          currentMessage.name === 'live-event' &&
          data &&
          typeof data === 'object' &&
          (data as { eventType?: string }).eventType === 'WEBINAR_CONFIG_UPDATED' &&
          (data as { resourceType?: string }).resourceType === 'WEBINAR' &&
          (data as { payloadJson?: unknown }).payloadJson
        ) {
          try {
            const rawPayload = (data as { payloadJson: unknown }).payloadJson;
            const payload = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;
            if (payload && typeof payload === 'object') {
              const p = payload as {
                displayParticipantsMinThreshold?: number;
                displayParticipantsCountEnabled?: boolean;
                chatMode?: 'PUBLIC' | 'PRIVATE' | 'DISABLED';
              };
              setWebinarConfigOverride((prev) => ({
                ...(prev ?? {}),
                ...(p.displayParticipantsMinThreshold !== undefined
                  ? { displayParticipantsMinThreshold: p.displayParticipantsMinThreshold }
                  : {}),
                ...(p.displayParticipantsCountEnabled !== undefined
                  ? { displayParticipantsCountEnabled: p.displayParticipantsCountEnabled }
                  : {}),
                ...(p.chatMode !== undefined ? { chatMode: p.chatMode } : {}),
              }));
            }
          } catch {
            // ignore parse error
          }
        }

        const chatModeChange = readChatModeChange(currentMessage.name, data);
        if (chatModeChange && effectiveSessionIdentifier) {
          dispatch(
            applyEvergreenChatModeChange({
              sessionIdentifier: effectiveSessionIdentifier,
              ...chatModeChange,
            }),
          );
        }

        if (
          currentMessage.name === 'live-event' &&
          data &&
          typeof data === 'object' &&
          webinarId &&
          effectiveSessionIdentifier &&
          token
        ) {
          const ev = data as {
            resourceType?: string;
            eventType?: string;
            resourceId?: unknown;
            payloadJson?: string;
          };
          const numericResourceIdFromEvent =
            typeof ev.resourceId === 'number' && Number.isFinite(ev.resourceId)
              ? ev.resourceId
              : typeof ev.resourceId === 'string' && /^\d+$/.test(ev.resourceId.trim())
                ? Number.parseInt(ev.resourceId.trim(), 10)
                : null;
          // Les slides sont incrustées dans le flux HLS par le compositor : le spectateur n'a rien
          // à charger ni à afficher, on ignore donc les events WEBINAR_SLIDE.

          // Ressources partagées : pas de canal/format dédié, le back diffuse via le pipeline
          // générique (live-event, resourceType SHARED_RESOURCE). Le DTO complet est embarqué dans
          // payloadJson au show (cf. SharedResourceServiceImpl.writeLastPayloadJson) ; au hide, le
          // payload peut être un simple {"action":"HIDE"} (auto-masquage d'exclusivité), donc on ne
          // dépend que de resourceId pour retirer la ressource.
          if (ev.resourceType === 'SHARED_RESOURCE' && numericResourceIdFromEvent != null) {
            const eventType = normalizeSharedResourceEventType(ev.eventType, ev.payloadJson);
            let resource: SharedResourcePublic | undefined;
            let payloadTimeNow: number | undefined;
            try {
              const parsedPayload = ev.payloadJson ? JSON.parse(ev.payloadJson) : null;
              if (typeof parsedPayload?.timeNow === 'number') {
                payloadTimeNow = parsedPayload.timeNow;
              }
              if (eventType === 'RESOURCE_SHOW') {
                const parsedResource = parsedPayload?.resource as SharedResourcePublic | undefined;
                if (parsedResource && typeof parsedResource.id === 'number') {
                  resource = parsedResource;
                }
              }
            } catch {
              // payload malformé : rien à afficher, pas de crash
            }
            if (eventType != null) {
              const playerHandle = videoHlsViewRef.current;
              const action = resolveSharedResourceEventAction({
                eventType,
                hasResource: resource != null,
                isEvergreen,
                hasPlayerHandle: playerHandle != null,
              });

              if (action === 'sync-on-player') {
                playerHandle?.handleResourceEvent({
                  eventType,
                  resourceType: 'SHARED_RESOURCE',
                  resourceId: numericResourceIdFromEvent,
                  timeNow: payloadTimeNow ?? Date.now(),
                  authorization: token,
                  resource,
                });
              } else if (action === 'refetch-current') {
                void dispatch(
                  fetchCurrentSharedResources({
                    webinarId,
                    sessionIdentifier: effectiveSessionIdentifier,
                    authorization: token,
                  }),
                );
              } else if (resource) {
                dispatch(applySharedResourceLiveEvent({ type: 'SHARED_RESOURCE_SHOW', resource }));
              } else {
                dispatch(
                  applySharedResourceLiveEvent({
                    type: 'SHARED_RESOURCE_HIDE',
                    resource: { id: numericResourceIdFromEvent },
                  }),
                );
              }
            }
          }
        }

        const isSessionCloseLiveEvent =
          currentMessage.name === 'live-event' &&
          data &&
          typeof data === 'object' &&
          (data as { eventType?: string }).eventType === 'SESSION_CLOSE' &&
          (data as { resourceType?: string }).resourceType === 'SESSION';

        const isSessionUpdateFromBackend =
          currentMessage.name === 'event-message' &&
          data &&
          typeof data === 'object' &&
          (data as { type?: string }).type === 'SESSION_UPDATE';

        if (isSessionCloseLiveEvent || isSessionUpdateFromBackend) {
          if (!webinarId || !effectiveSessionIdentifier || !token) {
            continue;
          }

          if (isStreamAvailabilityUpdate(currentMessage.name, data)) {
            void dispatch(
              fetchPublicPlayback({
                webinarId,
                sessionIdentifier: effectiveSessionIdentifier,
                authorization: token,
              }),
            );
          }

          if (isYouTube) {
            // On lève le flag AVANT le refetch (qui fait passer le statut à FINISHED), pour empêcher
            // le garde de rediriger immédiatement vers thank-you. Nettoyé juste après si ce n'est pas une clôture.
            dispatch(setYoutubeClosePending(true));
          }

          void dispatch(
            fetchPublicSession({
              webinarId: Number(webinarId),
              sessionIdentifier: effectiveSessionIdentifier,
            }),
          ).then((result) => {
            if (!fetchPublicSession.fulfilled.match(result)) {
              return;
            }
            const sessionStatus = result.payload?.session?.status;
            const shouldEndSession =
              isSessionCloseLiveEvent ||
              (isSessionUpdateFromBackend && sessionStatus === 'FINISHED');
            if (!shouldEndSession) {
              if (isYouTube) {
                dispatch(setYoutubeClosePending(false));
              }
              return;
            }
            if (webinarId != null) {
              clearChatHistoryJitterFlag(webinarId, effectiveSessionIdentifier);
            }
            const webiSession = result.payload?.session;

            const handleNavigate = () => {
              const backupUrl = webiSession?.criticalBackupUrl;
              if (backupUrl) {
                globalThis.location.replace(backupUrl);
              }
            };

            if (isEvergreen) {
              navigate(`/thank-you/${webinarId}/${effectiveSessionIdentifier}?token=${token}`, {
                replace: true,
              });
            }

            const timeNow = Date.now();
            videoHlsViewRef.current?.handleEndSessionEvent({
              timeNow,
              onNavigate: handleNavigate,
            });

            // Live YouTube : la lecture de l'attendee est en retard sur le direct. On diffère la
            // redirection vers thank-you du temps de retard mesuré (position de lecture), pour qu'il
            // voie la fin du live. Le garde reste bloqué par youtubeClosePending pendant ce temps.
            if (isYouTube && !isEvergreen) {
              const playbackNow = videoHlsViewRef.current?.getCurrentTimestamp() ?? null;
              const remainingMs =
                playbackNow != null
                  ? Math.min(Math.max(0, timeNow - playbackNow), YOUTUBE_CLOSE_MAX_DELAY_MS)
                  : 0;
              if (youtubeCloseTimeoutRef.current) {
                window.clearTimeout(youtubeCloseTimeoutRef.current);
              }
              youtubeCloseTimeoutRef.current = window.setTimeout(() => {
                dispatch(setYoutubeClosePending(false));
                navigate(`/thank-you/${webinarId}/${effectiveSessionIdentifier}?token=${token}`, {
                  replace: true,
                });
              }, remainingMs);
            }
          });
        }
      }
    },
    [
      channelName,
      isEvergreen,
      isYouTube,
      webinarId,
      effectiveSessionIdentifier,
      token,
      dispatch,
      navigate,
    ],
  );

  useChannel(channelName || '', onLiveChannelMessage);

  const session = publicSession?.session;

  const getEvergreenElapsedSeconds = useCallback(() => {
    if (!isEvergreen) return 0;
    const startedAt = session?.startedAt;
    if (!startedAt) return 0;
    const startUtc = DateTime.fromISO(startedAt, { zone: 'utc' });
    if (!startUtc.isValid) return 0;
    const nowUtc = DateTime.utc();
    return Math.max(0, Math.floor(nowUtc.diff(startUtc, 'seconds').seconds));
  }, [isEvergreen, session?.startedAt]);

  // YouTube n'a pas d'heure de segment HLS, et l'evergreen n'est jamais reconverti.
  const getChatPlaybackTimestamp = useCallback(
    () =>
      isYouTube || isEvergreen ? null : (videoHlsViewRef.current?.getCurrentTimestamp() ?? null),
    [isYouTube, isEvergreen],
  );

  /**
   * Seul point d'entrée pour déplacer la lecture evergreen : marque le saut comme voulu par le code
   * afin que le filet anti-scrub (plus bas) ne le confonde pas avec une avance du spectateur.
   */
  const seekEvergreenTo = useCallback((video: HTMLVideoElement, target: number) => {
    if (!Number.isFinite(target)) return;
    // Réassigner la même valeur n'émet pas de `seeked` : ne rien compter, sinon le compteur ne
    // redescendrait jamais et le prochain scrub réel passerait au travers.
    if (Math.abs(video.currentTime - target) < 0.05) return;
    evergreenProgrammaticSeeksRef.current += 1;
    video.currentTime = target;
  }, []);

  const { activeUrl: evergreenVideoUrl, hasFailed: hasEvergreenVideoFailed } =
    useEvergreenVideoSource({
      enabled: isEvergreen,
      videoRef: evergreenVideoRef,
      primaryUrl: playbackInfo?.mp4Url,
      fallbackUrl: playbackInfo?.fallbackVideoUrl,
      getResumePosition: getEvergreenElapsedSeconds,
      seekTo: seekEvergreenTo,
    });

  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;
    video.muted = isEvergreenMuted;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const tryPlay = () => {
      void video.play().catch(() => {
        video.muted = true;
        void video.play().catch(() => {});
      });
    };

    const seekAndPlay = (elapsedSeconds: number) => {
      seekEvergreenTo(video, clampEvergreenPosition(elapsedSeconds, video.duration));
      tryPlay();
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekAndPlay(getEvergreenElapsedSeconds());
    } else {
      const onLoadedMetadata = () => seekAndPlay(getEvergreenElapsedSeconds());
      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
    }
    video.addEventListener('canplay', tryPlay);

    retryTimer = setTimeout(() => {
      if (video.paused) {
        tryPlay();
      }
    }, 700);

    return () => {
      video.removeEventListener('canplay', tryPlay);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    isEvergreen,
    getEvergreenElapsedSeconds,
    evergreenVideoUrl,
    isEvergreenMuted,
    seekEvergreenTo,
  ]);

  // Recale la lecture sur l'avancement réel de la session (l'evergreen se joue comme un direct) puis
  // relance. Sert au retour d'onglet comme à la sortie de plein écran / mini-fenêtre.
  const resyncEvergreenPlayback = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    const video = evergreenVideoRef.current;
    if (!video) return;
    video.muted = isEvergreenMuted;

    seekEvergreenTo(video, clampEvergreenPosition(getEvergreenElapsedSeconds(), video.duration));

    void video.play().catch(() => {
      video.muted = true;
      void video.play().catch(() => {});
    });
  }, [isEvergreenMuted, getEvergreenElapsedSeconds, seekEvergreenTo]);

  /**
   * Filet anti-avance. `controls={false}` ne protège que la lecture en ligne : en plein écran natif
   * WebKit (Safari macOS, iPhone) comme en mini-fenêtre, le navigateur réaffiche sa propre barre de
   * progression. Sans ce filet le spectateur saute jusqu'à la fin du replay et rate offres, sondages
   * et quiz, tous déclenchés sur l'avancement de la session. On recale donc toute position que le
   * code n'a pas demandée.
   */
  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;

    const onSeeked = () => {
      if (evergreenProgrammaticSeeksRef.current > 0) {
        evergreenProgrammaticSeeksRef.current -= 1;
        return;
      }
      const target = resolveEvergreenSeekCorrection({
        currentTime: video.currentTime,
        expectedTime: getEvergreenElapsedSeconds(),
        duration: video.duration,
      });
      if (target === null) return;
      seekEvergreenTo(video, target);
      void video.play().catch(() => {});
    };

    video.addEventListener('seeked', onSeeked);
    return () => video.removeEventListener('seeked', onSeeked);
  }, [isEvergreen, evergreenVideoUrl, getEvergreenElapsedSeconds, seekEvergreenTo]);

  useEffect(() => {
    if (!isEvergreen) return;
    if (!session?.startedAt) return;

    // Mobile browsers freeze timers/playback in background; resync on return.
    const onVisibilityChange = () => {
      const video = evergreenVideoRef.current;

      if (document.visibilityState === 'hidden') {
        // Changement d'app sur mobile : garder la vidéo visible en mini-fenêtre plutôt que de couper.
        // Sauf sur iPhone, où la mini-fenêtre expose les sauts de 15 s (cf. le bouton PiP).
        if (
          video &&
          isMobileViewer() &&
          supportsElementFullscreen(evergreenFrameRef.current) &&
          isVideoPlaying(video)
        ) {
          void enterPictureInPicture(video);
        }
        return;
      }

      // Retour sur Welya : on quitte la mini-fenêtre pour réafficher la vidéo en grand, puis on recale.
      if (video) void exitPictureInPicture(video);
      resyncEvergreenPlayback();
    };
    const onFocus = () => resyncEvergreenPlayback();

    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('focus', onFocus);

    // If we mounted while already visible, ensure we’re synced immediately.
    resyncEvergreenPlayback();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('focus', onFocus);
    };
  }, [isEvergreen, session?.startedAt, resyncEvergreenPlayback]);

  // En evergreen, l'offre/sondage/quiz n'arrive que via un unique event Ably (cf. ChatView), sans
  // aucun rejeu. Si l'attendee a l'onglet en arrière-plan au moment du timecode, le navigateur gèle
  // la page et Ably peut perdre ce message : la ressource ne s'affiche alors jamais jusqu'à un
  // refresh manuel. Au retour au premier plan, on resynchronise l'état des ressources pour rattraper
  // un event manqué (le resync vidéo voisin ne touche, lui, qu'à la lecture). Petit jitter pour
  // éviter un pic de refetch simultané quand des milliers d'onglets reviennent en même temps.
  useEffect(() => {
    if (!isEvergreen) return;
    if (!webinarId || !effectiveSessionIdentifier || !token) return;

    let reconcileTimeout: number | null = null;
    const scheduleReconcile = () => {
      if (document.visibilityState !== 'visible') return;
      if (reconcileTimeout !== null) return;
      reconcileTimeout = window.setTimeout(() => {
        reconcileTimeout = null;
        // Refetch suffit : l'effet qui observe publicResourceStates ira charger l'offre passée
        // live, et un masquage éventuel se fait via le sélecteur activeOffer (qui exige liveOffer).
        dispatch(
          fetchPublicResourceState({
            webinarId,
            sessionIdentifier: effectiveSessionIdentifier,
            authorization: token,
          }),
        );
        // Same blind spot for a replayed chat mode change.
        dispatch(
          refreshEvergreenChatMode({ webinarId, sessionIdentifier: effectiveSessionIdentifier }),
        );
      }, Math.random() * 500);
    };

    document.addEventListener('visibilitychange', scheduleReconcile);
    globalThis.addEventListener('focus', scheduleReconcile);

    return () => {
      document.removeEventListener('visibilitychange', scheduleReconcile);
      globalThis.removeEventListener('focus', scheduleReconcile);
      if (reconcileTimeout !== null) window.clearTimeout(reconcileTimeout);
    };
  }, [isEvergreen, webinarId, effectiveSessionIdentifier, token, dispatch]);

  // Même angle mort pour la redirection de secours : un onglet gelé ne reçoit pas le message et
  // Ably ne le rejoue plus passé sa fenêtre de reprise. On relit donc la session au retour au
  // premier plan. Lecture seule assumée (pas de dispatch) pour ne pas faire basculer le statut du
  // live au passage, et même jitter que ci-dessus.
  useEffect(() => {
    if (!webinarId || !effectiveSessionIdentifier) return;

    let backupCheckTimeout: number | null = null;
    const scheduleBackupUrlCheck = () => {
      if (document.visibilityState !== 'visible') return;
      if (backupCheckTimeout !== null) return;
      backupCheckTimeout = window.setTimeout(() => {
        backupCheckTimeout = null;
        void liveService
          .getPublicSession(Number(webinarId), effectiveSessionIdentifier)
          .then((response) => {
            const backupUrl = response?.session?.criticalBackupUrl;
            if (backupUrl) {
              globalThis.location.replace(backupUrl);
            }
          })
          .catch(() => {
            // Le canal temps réel reste le chemin nominal.
          });
      }, Math.random() * 500);
    };

    document.addEventListener('visibilitychange', scheduleBackupUrlCheck);
    globalThis.addEventListener('focus', scheduleBackupUrlCheck);

    return () => {
      document.removeEventListener('visibilitychange', scheduleBackupUrlCheck);
      globalThis.removeEventListener('focus', scheduleBackupUrlCheck);
      if (backupCheckTimeout !== null) window.clearTimeout(backupCheckTimeout);
    };
  }, [webinarId, effectiveSessionIdentifier]);

  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;

    const syncFooterFromVideo = () => {
      if (video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      if (!Number.isFinite(video.currentTime)) return;
      let t = video.currentTime;
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) {
        t = Math.min(t, Math.max(0, d - 1e-3));
      }
      setEvergreenFooterSeconds(Math.max(0, Math.round(t)));
    };

    syncFooterFromVideo();
    video.addEventListener('timeupdate', syncFooterFromVideo);
    const id = window.setInterval(syncFooterFromVideo, 500);

    return () => {
      video.removeEventListener('timeupdate', syncFooterFromVideo);
      window.clearInterval(id);
    };
  }, [isEvergreen, evergreenVideoUrl]);

  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;

    let waitingTimer: ReturnType<typeof setTimeout> | null = null;
    const clearWaitingTimer = () => {
      if (waitingTimer) {
        clearTimeout(waitingTimer);
        waitingTimer = null;
      }
    };

    const settle = () => {
      clearWaitingTimer();
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        setIsEvergreenVideoLoading(false);
      }
    };
    // On ne réaffiche le spinner que si le rebuffer dure (>400 ms), pour éviter le flash
    // au retour d'onglet : le resync re-seek déclenche un `waiting` très bref.
    const markLoadingDeferred = () => {
      if (waitingTimer) return;
      waitingTimer = setTimeout(() => {
        waitingTimer = null;
        setIsEvergreenVideoLoading(true);
      }, 400);
    };

    settle();
    video.addEventListener('playing', settle);
    video.addEventListener('canplay', settle);
    video.addEventListener('timeupdate', settle);
    video.addEventListener('waiting', markLoadingDeferred);

    return () => {
      clearWaitingTimer();
      video.removeEventListener('playing', settle);
      video.removeEventListener('canplay', settle);
      video.removeEventListener('timeupdate', settle);
      video.removeEventListener('waiting', markLoadingDeferred);
    };
  }, [isEvergreen, evergreenVideoUrl]);

  const handleActivateEvergreenSound = () => {
    const video = evergreenVideoRef.current;
    if (!video) return;
    video.muted = false;
    setIsEvergreenMuted(false);
  };

  /** Affiche les contrôles au tap puis les masque seuls (player classique, pas de boutons permanents). */
  const revealEvergreenControls = () => {
    setShowEvergreenControls(true);
    if (hideEvergreenControlsTimerRef.current) clearTimeout(hideEvergreenControlsTimerRef.current);
    hideEvergreenControlsTimerRef.current = setTimeout(() => setShowEvergreenControls(false), 3000);
  };

  /** Desktop (souris) : on révèle les contrôles au survol ; le tactile reste au tap. */
  const canHoverRef = useRef(
    typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );
  const showEvergreenControlsOnHover = () => {
    if (hideEvergreenControlsTimerRef.current) clearTimeout(hideEvergreenControlsTimerRef.current);
    setShowEvergreenControls(true);
  };
  const hideEvergreenControlsOnHover = () => {
    if (hideEvergreenControlsTimerRef.current) clearTimeout(hideEvergreenControlsTimerRef.current);
    setShowEvergreenControls(false);
  };

  useEffect(() => {
    return () => {
      if (hideEvergreenControlsTimerRef.current)
        clearTimeout(hideEvergreenControlsTimerRef.current);
    };
  }, []);

  /** Bascule la mini-fenêtre via un geste utilisateur (seul moyen fiable d'entrer en PiP sur Android). */
  const toggleEvergreenPictureInPicture = () => {
    const video = evergreenVideoRef.current;
    if (!video) return;
    if (isInPictureInPicture(video)) {
      void exitPictureInPicture(video);
    } else {
      void enterPictureInPicture(video);
    }
  };

  /** Entrée / sortie de plein écran. L'entrée reste bloquée tant qu'une offre est à l'écran. */
  const toggleEvergreenFullscreen = () => {
    const video = evergreenVideoRef.current;
    if (!video) return;
    // Le cadre passant en plein écran n'a pas de bouton de sortie natif (contrairement au plein
    // écran vidéo iOS) : le même bouton doit permettre de revenir en arrière.
    if (isEvergreenFullscreen) {
      exitVideoFullscreen(video);
      return;
    }
    if (hasVisibleInteraction || !supportsElementFullscreen(evergreenFrameRef.current)) return;
    requestVideoFullscreen(video, evergreenFrameRef.current);
  };

  /** Une offre / sondage / quizz apparaît : on sort du plein écran pour qu'elle reste visible. */
  useEffect(() => {
    if (!isEvergreen || !hasVisibleInteraction) return;
    const video = evergreenVideoRef.current;
    if (video) exitVideoFullscreen(video);
  }, [isEvergreen, hasVisibleInteraction]);

  /** Support de la mini-fenêtre (affichage du bouton) + reprise à la sortie de PiP / plein écran. */
  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;

    // Bouton PiP réservé au mobile : sur desktop on ne garde que le plein écran. Exclu sur iPhone
    // (repéré à l'absence de Fullscreen API sur élément) : la mini-fenêtre WebKit y ouvre ses sauts
    // de 15 s, soit la même fuite que le plein écran natif.
    setCanUseEvergreenPip(
      isMobileViewer() &&
        supportsPictureInPicture(video) &&
        supportsElementFullscreen(evergreenFrameRef.current),
    );
    setIsEvergreenInPip(isInPictureInPicture(video));
    // Pas de bouton plein écran là où seule la balise `<video>` sait s'agrandir (iPhone) : le
    // lecteur natif y rouvrirait sa barre de progression, donc l'avance et la pause.
    setCanUseEvergreenFullscreen(supportsElementFullscreen(evergreenFrameRef.current));

    const onEnterPip = () => setIsEvergreenInPip(true);
    // Sortir de la mini-fenêtre ou du plein écran met la vidéo en pause sur iOS : on relance en se
    // recalant sur l'avancement de la session, sinon le spectateur repart en retard.
    const onLeavePip = () => {
      setIsEvergreenInPip(false);
      resyncEvergreenPlayback();
    };
    const onWebkitPresentationChange = () => {
      const inPip = isInPictureInPicture(video);
      setIsEvergreenInPip(inPip);
      if (!inPip) resyncEvergreenPlayback();
    };
    const onFullscreenChange = () => {
      const inFullscreen = !!getFullscreenElement();
      setIsEvergreenFullscreen(inFullscreen);
      if (!inFullscreen) resyncEvergreenPlayback();
    };
    const onWebkitEndFullscreen = () => resyncEvergreenPlayback();

    video.addEventListener('enterpictureinpicture', onEnterPip);
    video.addEventListener('leavepictureinpicture', onLeavePip);
    video.addEventListener('webkitpresentationmodechanged', onWebkitPresentationChange);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    // iPad et vieux Safari macOS n'émettent que la forme préfixée.
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    video.addEventListener('webkitendfullscreen', onWebkitEndFullscreen);

    return () => {
      video.removeEventListener('enterpictureinpicture', onEnterPip);
      video.removeEventListener('leavepictureinpicture', onLeavePip);
      video.removeEventListener('webkitpresentationmodechanged', onWebkitPresentationChange);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      video.removeEventListener('webkitendfullscreen', onWebkitEndFullscreen);
    };
  }, [isEvergreen, evergreenVideoUrl, resyncEvergreenPlayback]);

  const displayParticipantsMinThreshold =
    webinarConfigOverride?.displayParticipantsMinThreshold ??
    session?.displayParticipantsMinThreshold ??
    0;
  const displayParticipantsCountEnabled =
    webinarConfigOverride?.displayParticipantsCountEnabled ??
    session?.displayParticipantsCountEnabled ??
    true;
  const chatMode = resolveAttendeeChatMode({
    isEvergreen,
    evergreenAttendeeChatMode: replayedChatModeFor(
      evergreenChatModeSync.replayed,
      effectiveSessionIdentifier,
    ),
    liveOverrideChatMode: webinarConfigOverride?.chatMode,
    sessionChatMode: session?.chatMode,
  });
  const isReady = !!webinarId && !!sessionIdentifier && !!token && !!session && !!visualIdentity;
  const entryPending = isLiveEntryPending({ isReady, status: session?.status });
  const entryTimedOut = useElapsedFlag(LIVE_ENTRY_TIMEOUT_MS, entryPending);
  const entryFailed = Boolean(publicSessionError) || entryTimedOut;

  if (publicSession && publicSession.broadcastAllowed === false) {
    return <SplashScreen label={t('live.webinarFinished')} fullscreen />;
  }

  // `!isReady` est répété pour que TypeScript garde le rétrécissement de type qu’il tire de cet
  // alias ; la condition reste équivalente à `entryPending`, qui le contient déjà.
  if (!isReady || entryPending) {
    // Le chargement doit toujours céder la place à un état terminal : une erreur remontée, ou le
    // filet de temps si aucune requête ne se termine ni n'échoue.
    if (entryFailed) {
      return <LiveEntryError step="session" />;
    }
    return <SplashScreen label={t('live.loading')} />;
  }

  return (
    <div
      // className="h-screen flex items-stretch justify-center gap-6"
      className=" h-svh  max-w-[2400px] mx-auto w-full flex flex-col lg:flex-row lg:justify-center lg:gap-6 overflow-hidden min-h-0 compact-landscape:flex-row compact-landscape:justify-center compact-landscape:gap-4 compact-landscape:pl-[env(safe-area-inset-left)] compact-landscape:pr-[env(safe-area-inset-right)]"
      style={{ backgroundColor: visualIdentity?.backgroundColor ?? '#FFFFFF' }}
    >
      {!isMobile &&
        hasReached2Min &&
        (exitIntentDialogOpen || canShowExitIntentPopup) &&
        publicQuitPage &&
        publicQuitPage?.showPopup && (
          <Dialog
            open={exitIntentDialogOpen}
            onOpenChange={(open) => !open && handleExitIntentClose()}
          >
            <DialogHeader className="hidden">
              <DialogTitle>{t('live.exitDialogTitle')}</DialogTitle>
            </DialogHeader>
            <DialogContent
              className="p-0 max-w-2xl w-fit max-h-[90vh] overflow-y-auto border-none shadow-none bg-transparent"
              hideCloseButton={false}
            >
              <QuitCard
                quitPage={publicQuitPage}
                onSubmit={handleExitIntentSubmit}
                onCancel={handleExitIntentClose}
                visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                webinarId={webinarId ?? undefined}
                sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || undefined}
                authorization={token ?? undefined}
              />
            </DialogContent>
          </Dialog>
        )}
      {/* left section — responsive lg: ~2/3 width, min 300px, max 920px */}
      <div className="flex shrink-0 flex-col gap-4 lg:m-0 lg:min-w-[300px] lg:flex-2 lg:justify-between lg:pl-6 lg:py-6 lg:h-full lg:overflow-y-hidden compact-landscape:min-w-0 compact-landscape:flex-2 compact-landscape:justify-between compact-landscape:h-full compact-landscape:overflow-y-hidden">
        <div className="flex flex-col items-center gap-4 lg:flex-1 lg:min-h-0 lg:h-full compact-landscape:flex-1 compact-landscape:min-h-0 compact-landscape:h-full">
          <div className="w-full relative lg:flex-1 lg:min-h-0 lg:h-full lg:flex lg:items-stretch lg:justify-stretch compact-landscape:flex-1 compact-landscape:min-h-0 compact-landscape:h-full compact-landscape:flex compact-landscape:items-stretch compact-landscape:justify-stretch">
            {/* {isEvergreen && playbackInfo?.mp4Url ? ( */}
            {isEvergreen ? (
              <>
                {/* Même cadrage que le live (VideoHlsView) : cadre 16:9 à la couleur de scène +
                    boîte 16:9 overflow-hidden + overscan origin-top-left qui pousse le liseré egress
                    (droite/bas) baké dans le mp4 hors cadre. Plus de bandes noires. */}
                <div
                  ref={evergreenFrameRef}
                  className={cn(
                    'w-full max-h-full lg:h-full min-w-0 min-h-0 relative overflow-hidden rounded-none lg:rounded-2xl aspect-video',
                    'compact-landscape:h-full compact-landscape:rounded-2xl',
                    // C'est ce cadre, et non la balise `<video>`, qui passe en plein écran (sinon
                    // WebKit rouvre ses contrôles natifs) : il doit alors remplir l'écran, la boîte
                    // interne gardant le 16:9.
                    '[&:fullscreen]:aspect-auto [&:fullscreen]:h-full [&:fullscreen]:max-h-none [&:fullscreen]:rounded-none',
                  )}
                  style={{
                    backgroundColor: visualIdentity?.backgroundColor ?? '#FFFFFF',
                  }}
                  onClick={() => {
                    if (!canHoverRef.current) revealEvergreenControls();
                  }}
                  onMouseEnter={() => {
                    if (canHoverRef.current) showEvergreenControlsOnHover();
                  }}
                  onMouseMove={() => {
                    // En plein écran le cadre occupe tout l'écran : le curseur ne peut plus en
                    // sortir, donc aucun `mouseenter` ne se redéclenche et les contrôles masqués
                    // (Safari émet un `mouseleave` pendant la transition) resteraient inaccessibles.
                    if (canHoverRef.current && isEvergreenFullscreen) revealEvergreenControls();
                  }}
                  onMouseLeave={() => {
                    if (canHoverRef.current) hideEvergreenControlsOnHover();
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    {/* En plein écran, on laisse la boîte prendre tout l'écran et c'est
                        `object-contain` qui préserve le ratio : sur un écran plus large que 16:9,
                        garder `w-full aspect-video` ferait déborder la hauteur hors de l'écran. */}
                    <div className="w-full max-h-full aspect-video flex items-center justify-center">
                      <div className={evergreenVideoBoxClassName(isEvergreenFullscreen)}>
                        <video
                          ref={evergreenVideoRef}
                          src={evergreenVideoUrl}
                          className="absolute inset-0 w-full h-full object-contain origin-top-left scale-[1.005]"
                          playsInline
                          controls={false}
                          controlsList="nodownload noremoteplayback"
                          onContextMenu={(e) => e.preventDefault()}
                          autoPlay
                          muted={isEvergreenMuted}
                        >
                          <track
                            kind="captions"
                            srcLang={webinarLng}
                            label={t('live.noCaptionsTrack')}
                          />
                        </video>
                      </div>
                    </div>
                  </div>
                  {hasEvergreenVideoFailed && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/60 px-6 text-center">
                      <p className="text-white text-sm md:text-base font-medium">
                        {t('live.evergreenVideoUnavailable')}
                      </p>
                    </div>
                  )}
                  {isEvergreenVideoLoading && !hasEvergreenVideoFailed && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 pointer-events-none">
                      <Loader2 className="animate-spin size-10 text-white" />
                      <p className="text-white text-sm md:text-base font-medium">
                        {t('live.loadingInProgress')}
                      </p>
                    </div>
                  )}
                  {showEvergreenControls &&
                    !isEvergreenMuted &&
                    !isEvergreenVideoLoading &&
                    !hasVisibleInteraction && (
                      <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
                        {/* Calque calé sur le rectangle 16:9 réel de la vidéo (object-contain) : en
                            desktop le conteneur est plus haut que l'image, ce calque évite que les
                            contrôles tombent dans la bande du bas. En plein écran on s'aligne sur
                            l'écran entier, sinon sur un écran très large le calque déborde et
                            emmène les boutons hors du champ visible. */}
                        <div className="w-full max-h-full aspect-video flex items-center justify-center">
                          <div className={evergreenControlsBoxClassName(isEvergreenFullscreen)}>
                            <div className="absolute bottom-3 right-3 flex items-center gap-2 pointer-events-auto">
                              {canUseEvergreenPip && (
                                <button
                                  type="button"
                                  onClick={toggleEvergreenPictureInPicture}
                                  aria-label={tCommon('liveSettings.pictureInPicture')}
                                  title={tCommon('liveSettings.pictureInPicture')}
                                  aria-pressed={isEvergreenInPip}
                                  className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                                >
                                  <PictureInPicture2 className="size-5 text-white" aria-hidden />
                                </button>
                              )}
                              {canUseEvergreenFullscreen && (
                                <button
                                  type="button"
                                  onClick={toggleEvergreenFullscreen}
                                  aria-label={tCommon(
                                    isEvergreenFullscreen
                                      ? 'liveSettings.exitFullscreen'
                                      : 'liveSettings.fullscreen',
                                  )}
                                  title={tCommon(
                                    isEvergreenFullscreen
                                      ? 'liveSettings.exitFullscreen'
                                      : 'liveSettings.fullscreen',
                                  )}
                                  className="flex items-center justify-center rounded-full bg-black/55 p-2 backdrop-blur-sm transition-colors hover:bg-black/75 cursor-pointer"
                                >
                                  {isEvergreenFullscreen ? (
                                    <Minimize2 className="size-5 text-white" aria-hidden />
                                  ) : (
                                    <Maximize2 className="size-5 text-white" aria-hidden />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
                {isEvergreenMuted && !isEvergreenVideoLoading && (
                  <button
                    onClick={handleActivateEvergreenSound}
                    className="absolute hover:bg-black/80 cursor-pointer flex-col gap-6 left-1/2 flex flex-wrap items-center justify-center -translate-x-1/2 z-10 top-1/2 -translate-y-1/2 w-full md:w-xl h-full lg:h-80 shadow-2xl backdrop-blur-sm overflow-x-auto bg-black/70 rounded-3xl p-2 md:p-8"
                  >
                    <div
                      className="flex items-center justify-center p-3 rounded-xl"
                      style={{
                        backgroundColor: visualIdentity?.secondaryBackgroundColor
                          ? `${visualIdentity.secondaryBackgroundColor}20`
                          : '#ffffff',
                      }}
                    >
                      <Volume2
                        className="size-9"
                        style={{ color: visualIdentity?.secondaryBackgroundColor ?? '#ffffff' }}
                      />
                    </div>
                    <p className="text-white text-xl md:text-3xl font-bold wrap-break-word w-full">
                      {t('live.youtube.unmute.title')}
                    </p>
                    <p className="text-white text-xs md:text-sm font-normal wrap-break-word w-full">
                      {t('live.youtube.unmute.subtitle')}
                    </p>
                  </button>
                )}
              </>
            ) : isYouTube ? (
              <YouTubeWatchView
                ref={videoHlsViewRef}
                target={target}
                youtubeUrl={youtubeUrl}
                startedAtMs={sessionStartedAtMs}
                actualStartTimeMs={youtubeActualStartTimeMs}
                onSeekToLive={reconcileToLiveResourceState}
              />
            ) : !isEvergreen ? (
              <VideoHlsView
                ref={videoHlsViewRef}
                target={target}
                visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                interactionVisible={hasVisibleInteraction}
              />
            ) : null}
            {/* reaction animation */}
            {currentReactions?.map((reaction) => (
              <div
                key={reaction.id}
                className="absolute hidden md:block bottom-0 translate-y-[-50%] pointer-events-none z-50 animate-rise-up-tiktok"
                style={{
                  left: `${reaction?.left ?? 0}%`,
                  ['--drift' as string]: `${reaction?.drift ?? 0}px`,
                  ['--rotate-start' as string]: `${reaction?.rotateStart ?? 0}deg`,
                  ['--rotate-end' as string]: `${reaction?.rotateEnd ?? 0}deg`,
                }}
              >
                <div className="text-2xl">{reaction?.text ?? ''}</div>
              </div>
            ))}
          </div>
          <div className="w-full lg:flex shrink-0 hidden justify-center md:justify-between lg:justify-start gap-4">
            {showSpectatorReactions && channelName && (
              <LiveReactionSection
                channelName={channelName}
                userId={chatUserId}
                userName={chatUserName}
                attendeeSessionIdentifier={effectiveSessionIdentifier || sessionIdentifier}
                attendeeAuthorization={token}
                webinarId={webinarId ?? 0}
                visualIdentity={visualIdentity as WorkspaceVisualIdentity}
              />
            )}
            <div
              className=" items-center justify-between hidden  md:block lg:hidden "
              // className="flex items-center justify-between"
            >
              {channelName && (
                <FooterBar
                  liveParticipantCount={liveParticipantCount}
                  liveStartAt={session?.startedAt ?? undefined}
                  isAttendee
                  isEvergreen={isEvergreen}
                  evergreenElapsedDisplaySeconds={
                    isEvergreen && session?.startedAt ? evergreenFooterSeconds : undefined
                  }
                  visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                  displayParticipantsMinThreshold={displayParticipantsMinThreshold}
                  displayParticipantsCountEnabled={displayParticipantsCountEnabled}
                  onLiveDurationReached2Min={setHasReached2Min}
                  formattedPlannedDate={formattedPlannedDate}
                />
              )}
            </div>
          </div>
        </div>
        <div className="hidden lg:block lg:items-center lg:shrink-0 w-full">
          {channelName && (
            <FooterBar
              liveParticipantCount={liveParticipantCount}
              liveStartAt={session?.startedAt ?? undefined}
              isAttendee
              isEvergreen={isEvergreen}
              evergreenElapsedDisplaySeconds={
                isEvergreen && session?.startedAt ? evergreenFooterSeconds : undefined
              }
              visualIdentity={visualIdentity as WorkspaceVisualIdentity}
              displayParticipantsMinThreshold={displayParticipantsMinThreshold}
              displayParticipantsCountEnabled={displayParticipantsCountEnabled}
              onLiveDurationReached2Min={setHasReached2Min}
              formattedPlannedDate={formattedPlannedDate}
            />
          )}
        </div>
      </div>
      {/* right section — responsive lg: ~1/3 width, min 280px, max 456px */}
      <div
        className={cn(
          'block flex-1 min-h-0',
          // h-svh et non h-screen : la racine est en h-svh, h-screen y ajoutait la hauteur des
          // barres de navigateur mobile et faisait déborder la colonne.
          'lg:min-w-[280px] lg:max-w-[456px] lg:flex-1 lg:pr-6 lg:h-svh',
          // Pas de gouttière au bord en paysage : le chat va d'un bord à l'autre, le retrait de
          // sécurité de l'encoche est porté par la racine.
          'compact-landscape:min-w-[280px] compact-landscape:max-w-[456px] compact-landscape:flex-1 compact-landscape:h-full',
        )}
      >
        <div className="h-full flex flex-col  min-h-0 relative">
          {channelName ? (
            <ChatView
              webinarId={webinarId}
              isAttendee
              chatMode={chatMode}
              channelName={channelName}
              userId={chatUserId}
              userName={chatUserName}
              attendeeSessionIdentifier={effectiveSessionIdentifier || sessionIdentifier}
              attendeeAuthorization={token}
              isEvergreen={isEvergreen}
              liveParticipantCountSync={liveParticipantCount}
              getPlaybackTimestamp={getChatPlaybackTimestamp}
              onResourceEvent={(event) => {
                if (!event) return;
                videoHlsViewRef.current?.handleResourceEvent({
                  ...event,
                  authorization: token ?? '',
                });
              }}
              onSurveyResultsEvent={(event) => {
                if (event) {
                  videoHlsViewRef.current?.handleSurveyResultsEvent(event);
                }
              }}
              visualIdentity={visualIdentity as WorkspaceVisualIdentity}
            />
          ) : null}
        </div>
      </div>
      {/* Interaction (offre / sondage / quizz) — entre le chat et le timer en mobile/tablette */}
      {activeSurvey?.isVisible &&
        publicSurvey &&
        publicSession?.visualIdentity &&
        publicSession?.session &&
        publicSurvey.id === activeSurvey.resourceId &&
        !hasUserSubmittedSurvey && (
          <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
            <SurveyCard
              survey={publicSurvey}
              visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
              isAttendee
              showResults={activeSurvey.showResults ?? false}
              webinarId={webinarId ?? undefined}
              workspaceId={publicSession?.session?.workspaceId}
              sessionId={publicSession?.session?.id}
              authorization={token ?? undefined}
              sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || undefined}
              liveParticipantCountSync={liveParticipantCount}
            />
          </div>
        )}

      {activeQuiz?.isVisible &&
        publicQuiz &&
        publicSession?.visualIdentity &&
        publicSession?.session &&
        publicQuiz.id === activeQuiz.resourceId &&
        !hasUserSubmittedQuiz && (
          <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
            {publicQuiz.enabledDuringLive === false ||
            (isEvergreen && publicEvergreen?.chatMode === 'DISABLED') ? (
              <QuizCard
                quiz={publicQuiz}
                visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                isAttendee
                webinarId={webinarId ?? undefined}
                sessionId={publicSession?.session?.id}
                authorization={token ?? undefined}
                sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || undefined}
                liveParticipantCountSync={liveParticipantCount}
              />
            ) : (
              <div className="flex flex-col justify-between">
                <div className="flex-1 flex items-center justify-center px-0 md:px-4">
                  <QuizCard
                    quiz={publicQuiz}
                    visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
                    isAttendee
                    webinarId={webinarId ?? undefined}
                    sessionId={publicSession?.session?.id}
                    authorization={token ?? undefined}
                    sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || undefined}
                    liveParticipantCountSync={liveParticipantCount}
                  />
                </div>
              </div>
            )}
          </div>
        )}

      {/* Embed ouvert : ChatView l'affiche à la place du chat, la carte n'a plus lieu d'être. */}
      {activeOffer?.isVisible &&
        publicOffer &&
        publicSession?.visualIdentity &&
        publicOffer.id === activeOffer.resourceId &&
        !shouldShowOfferEmbed(openedOfferEmbedId, publicOffer) && (
          <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
            <OfferCard
              offer={publicOffer}
              visualIdentity={publicSession.visualIdentity as WorkspaceVisualIdentity}
              attendeeView
              webinarId={webinarId ?? undefined}
              sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || undefined}
              authorization={token ?? undefined}
              onEmbedOpen={() => dispatch(openOfferEmbed(publicOffer.id))}
            />
          </div>
        )}

      {webinarId && (effectiveSessionIdentifier || sessionIdentifier) && token && (
        <div className="lg:hidden compact-landscape:hidden block shrink-0 overflow-y-auto max-h-[50dvh]">
          <SharedResourceViewer
            webinarId={webinarId}
            sessionIdentifier={effectiveSessionIdentifier || sessionIdentifier || ''}
            authorization={token}
            visualIdentity={visualIdentity as WorkspaceVisualIdentity}
          />
        </div>
      )}
      <div
        className={cn(
          'bottom-0 mx-0 my-0 lg:hidden compact-landscape:hidden flex justify-center lg:shrink-0 w-full pb-[max(0px,env(safe-area-inset-bottom))]',
        )}
      >
        {channelName && (
          <FooterBar
            liveParticipantCount={liveParticipantCount}
            liveStartAt={session?.startedAt ?? undefined}
            isAttendee
            isEvergreen={isEvergreen}
            evergreenElapsedDisplaySeconds={
              isEvergreen && session?.startedAt ? evergreenFooterSeconds : undefined
            }
            visualIdentity={visualIdentity as WorkspaceVisualIdentity}
            displayParticipantsMinThreshold={displayParticipantsMinThreshold}
            displayParticipantsCountEnabled={displayParticipantsCountEnabled}
            onLiveDurationReached2Min={setHasReached2Min}
            formattedPlannedDate={formattedPlannedDate}
          />
        )}
      </div>
    </div>
  );
}
